import React, { useState, useEffect } from 'react'
import {
  BuildingLibraryIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  BuildingOfficeIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'
import { institucionesService, Institucion, InstitucionCrear, InstitucionActualizar } from '../../../services/institucionesService'
import { resolverPresentacionErrorApi } from '../../../services/apiError'
import ModalInstitucion from './ModalInstitucion'
import { useToast } from '../../../ganchos/interfaz-usuario/usarNotificaciones'
import { usePermisos } from '../../../ganchos/usePermisos'

const PaginaGestionInstituciones: React.FC = () => {
  const [instituciones, setInstituciones] = useState<Institucion[]>([])
  const [cargando, setCargando] = useState(true)
  const [filtro, setFiltro] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalTipo, setModalTipo] = useState<'ver' | 'editar' | 'crear' | 'eliminar'>('crear')
  const [institucionSeleccionada, setInstitucionSeleccionada] = useState<Institucion | null>(null)
  const { showToast } = useToast()
  const { tienePermiso } = usePermisos()

  const cargarInstituciones = async () => {
    setCargando(true)
    try {
      const data = await institucionesService.obtenerInstituciones()
      setInstituciones(data)
    } catch (error) {
      console.error('Error al cargar instituciones:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al cargar la lista de instituciones',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          unauthorized: 'Sesión no válida',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message
      })
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    cargarInstituciones()
  }, [])

  const abrirModal = (tipo: 'ver' | 'editar' | 'crear' | 'eliminar', institucion?: Institucion) => {
    setModalTipo(tipo)
    setInstitucionSeleccionada(institucion || null)
    setModalOpen(true)
  }

  const manejarGuardar = async (data: InstitucionCrear | InstitucionActualizar) => {
    try {
      if (modalTipo === 'crear') {
        await institucionesService.crearInstitucion(data as InstitucionCrear)
        showToast({
          type: 'success',
          title: 'Éxito',
          message: 'Institución creada exitosamente'
        })
      } else if (modalTipo === 'editar' && institucionSeleccionada) {
        if (!institucionSeleccionada.id && !institucionSeleccionada.id_constitucion && !institucionSeleccionada.id_organizacion) {
            throw new Error('ID de institución no válido')
        }
        
        // Filtrar datos para enviar solo lo necesario
        const datosActualizar: InstitucionActualizar = {
            nombre: data.nombre,
            descripcion: data.descripcion,
            tipo: data.tipo,
            contacto_email: data.contacto_email,
            contacto_telefono: data.contacto_telefono,
            activo: (data as any).activo // Asegurar que el campo activo se envíe si existe
        }

        await institucionesService.actualizarInstitucion(institucionSeleccionada, datosActualizar)
        showToast({
          type: 'success',
          title: 'Éxito',
          message: institucionSeleccionada.origen === 'organizacion' ? 'Empresa actualizada exitosamente' : 'Institución actualizada exitosamente'
        })
      }
      cargarInstituciones()
    } catch (error: unknown) {
      console.error('Error al guardar:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al guardar la institución',
        titulosPorCodigo: {
          validation_error: 'Datos inválidos',
          forbidden: 'Acceso denegado',
          not_found: 'Institución no encontrada',
          conflict: 'Institución duplicada',
        },
        mensajesPorCodigo: {
          validation_error: 'Verifica la información capturada antes de guardar la institución.',
          forbidden: 'No tienes permisos para crear o actualizar instituciones.',
          not_found: 'La institución que intentas editar ya no está disponible.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message
      })
      throw error
    }
  }

  const manejarEliminar = async (entidad: Institucion) => {
    try {
      if (!entidad.id && !entidad.id_constitucion && !entidad.id_organizacion) {
        throw new Error('ID de institución no válido')
      }
      await institucionesService.eliminarInstitucion(entidad)
      showToast({
        type: 'success',
        title: 'Éxito',
        message: entidad.origen === 'organizacion' ? 'Empresa eliminada exitosamente' : 'Institución eliminada exitosamente'
      })
      cargarInstituciones()
    } catch (error: unknown) {
      console.error('Error al eliminar:', error)
      const presentacion = resolverPresentacionErrorApi(error, {
        tituloFallback: 'Error',
        mensajeFallback: 'Error al eliminar la institución',
        titulosPorCodigo: {
          forbidden: 'Acceso denegado',
          not_found: 'Institución no encontrada',
        },
        mensajesPorCodigo: {
          forbidden: 'No tienes permisos para eliminar instituciones.',
          not_found: 'La institución que intentas eliminar ya no existe.',
        },
      })
      showToast({
        type: 'error',
        title: presentacion.title,
        message: presentacion.message
      })
      throw error
    }
  }

  const institucionesFiltradas = instituciones.filter(inst =>
    inst.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
    inst.tipo?.toLowerCase().includes(filtro.toLowerCase())
  )

  const getIconoTipo = (tipo?: string) => {
    switch (tipo?.toLowerCase()) {
      case 'universidad':
        return <AcademicCapIcon className="h-5 w-5 text-blue-500" />
      case 'empresa':
        return <BuildingOfficeIcon className="h-5 w-5 text-purple-500" />
      default:
        return <BuildingLibraryIcon className="h-5 w-5 text-gray-500" />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BuildingLibraryIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            Gestión de Instituciones
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Administra las universidades, escuelas y empresas registradas en el sistema.
          </p>
        </div>
        {tienePermiso('instituciones:crear') && (
          <button
            onClick={() => abrirModal('crear')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Nueva Institución
          </button>
        )}
      </div>

      {/* Filtros y Búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white"
            placeholder="Buscar por nombre o tipo..."
          />
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <FunnelIcon className="-ml-1 mr-2 h-5 w-5 text-gray-400" />
          Filtros
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Institución
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contacto
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estructura
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {cargando ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Cargando instituciones...
                  </td>
                </tr>
              ) : institucionesFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No se encontraron instituciones
                  </td>
                </tr>
              ) : (
                institucionesFiltradas.map((inst) => (
                  <tr key={inst.id ?? inst.id_constitucion ?? inst.id_organizacion} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          {getIconoTipo(inst.tipo)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {inst.nombre}
                          </div>
                          {inst.descripcion && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {inst.descripcion}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 capitalize">
                        {inst.tipo || 'Desconocido'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{inst.contacto_email || '-'}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{inst.contacto_telefono || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        inst.activo
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {inst.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {inst.miembros_count ?? 0} miembros
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {inst.grupos_count ?? 0} grupos
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => abrirModal('ver', inst)}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                          title="Ver Detalles"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {tienePermiso('instituciones:editar') && (
                          <button
                            onClick={() => abrirModal('editar', inst)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Editar"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        )}
                        {tienePermiso('instituciones:eliminar') && (
                          <button
                            onClick={() => abrirModal('eliminar', inst)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <ModalInstitucion
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        tipo={modalTipo}
        institucion={institucionSeleccionada}
        onGuardar={manejarGuardar}
          onEliminar={() => manejarEliminar(institucionSeleccionada as Institucion)}
      />
    </div>
  )
}

export default PaginaGestionInstituciones
